import { useEffect, useState } from "react";
import { Users, Eye, MousePointerClick, Euro, ShoppingBag, CalendarDays, Globe, Search } from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, BarChart, Bar } from "recharts";
import { api, imgUrl, eur } from "@/lib/api";
import { BrandBadge } from "@/components/shop/Badges";
import { LowStockAlert } from "@/pages/admin/LowStockAlert";

const Kpi = ({ icon: Icon, label, value, tid }) => (
  <div className="border border-[#3B4157] bg-[#292D3C] p-5">
    <div className="flex items-center justify-between text-slate-500"><span className="text-[10px] uppercase tracking-[0.2em] font-mono-x">{label}</span><Icon className="w-4 h-4 text-orange-500" /></div>
    <div data-testid={tid} className="font-display font-black text-4xl mt-3">{value}</div>
  </div>
);

const PERIODS = [[1, "Šiandien"], [7, "7 dienos"], [30, "30 dienų"]];

const Sources = ({ items }) => {
  const max = Math.max(1, ...items.map((s) => s.visitors));
  return (
    <div data-testid="traffic-sources" className="border border-[#3B4157] bg-[#292D3C] p-5">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] font-mono-x text-slate-500 mb-4"><Globe className="w-3.5 h-3.5 text-orange-500" /> Iš kur atėjo</div>
      {!items.length && <p className="text-sm text-slate-500">Dar nėra duomenų</p>}
      <div className="space-y-2.5">
        {items.map((s) => (
          <div key={s.source} data-testid={`source-${s.source}`} className="text-sm">
            <div className="flex justify-between"><span>{s.source}</span><span className="font-mono-x text-orange-500">{s.visitors}</span></div>
            <div className="h-1.5 bg-[#1F2230] mt-1"><div className="h-full bg-orange-500" style={{ width: `${(s.visitors / max) * 100}%` }} /></div>
          </div>
        ))}
      </div>
    </div>
  );
};

const Searches = ({ items }) => (
  <div data-testid="search-terms" className="border border-[#3B4157] bg-[#292D3C] overflow-x-auto">
    <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] font-mono-x text-slate-500 p-5 pb-0"><Search className="w-3.5 h-3.5 text-orange-500" /> Ko ieškojo svetainėje</div>
    {!items.length ? <p className="text-sm text-slate-500 p-5">Per šį laikotarpį paieškų nebuvo</p> : (
      <table className="w-full text-sm mt-4">
        <thead><tr className="text-left text-slate-500 text-xs uppercase tracking-wider border-b border-[#3B4157]">
          <th className="p-3 pl-5">Paieška</th><th className="p-3 text-right">Kartų</th><th className="p-3 text-right">Lankytojų</th><th className="p-3 pr-5 text-right">Rasta sėdynių</th>
        </tr></thead>
        <tbody>
          {items.map((s) => (
            <tr key={s.q} data-testid={`search-row-${s.q}`} className="border-b border-[#3B4157] last:border-0">
              <td className="p-3 pl-5">{s.q}</td>
              <td className="p-3 text-right font-mono-x text-orange-500">{s.count}</td>
              <td className="p-3 text-right font-mono-x">{s.visitors}</td>
              <td className="p-3 pr-5 text-right font-mono-x">{s.results ? s.results : <span className="text-red-400">0 – nerasta</span>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    )}
  </div>
);

const tip = { contentStyle: { background: "#292D3C", border: "1px solid #3B4157", fontSize: 12 } };

export default function AdminDashboard() {
  const [d, setD] = useState(null);
  const [days, setDays] = useState(7);
  useEffect(() => {
    api.get("/admin/analytics", { params: { days } }).then((r) => setD(r.data));
  }, [days]);
  if (!d) return <div className="text-slate-500">Kraunama...</div>;

  const top = d.products.filter((p) => p.period_views).slice(0, 6).map((p) => ({ name: p.name.length > 18 ? p.name.slice(0, 18) + "…" : p.name, views: p.period_views }));

  return (
    <div data-testid="admin-dashboard" className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <h1 className="font-display font-black uppercase text-4xl tracking-tight">Statistika</h1>
        <div className="flex border border-[#3B4157]">
          {PERIODS.map(([n, label]) => (
            <button key={n} data-testid={`period-${n}`} onClick={() => setDays(n)}
              className={`h-10 px-4 text-xs uppercase tracking-wider font-display font-semibold transition-colors ${days === n ? "bg-orange-500 text-[#1F2230]" : "text-slate-400 hover:text-orange-500"}`}>{label}</button>
          ))}
        </div>
      </div>
      <p className="text-xs text-slate-500 -mt-5">Lankytojai skaičiuojami anonimiškai, be slapukų. Botai ir paieškos robotai neįskaičiuojami.</p>
      <LowStockAlert items={d.low_stock} showLink />
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
        <Kpi icon={Users} label="Lankytojai" value={d.visitors} tid="kpi-unique-visitors" />
        <Kpi icon={CalendarDays} label="Lankytojai šiandien" value={d.visitors_today} tid="kpi-visitors-today" />
        <Kpi icon={MousePointerClick} label="Puslapių peržiūros" value={d.pageviews} tid="kpi-pageviews" />
        <Kpi icon={Eye} label="Sėdynių peržiūros" value={d.product_views} tid="kpi-product-views" />
        <Kpi icon={ShoppingBag} label="Apmokėti užsakymai" value={d.orders_paid} tid="kpi-orders-paid" />
        <Kpi icon={Euro} label="Pajamos" value={eur(d.revenue)} tid="kpi-revenue" />
      </div>
      <div className="grid lg:grid-cols-5 gap-4">
        <div className="lg:col-span-3 border border-[#3B4157] bg-[#292D3C] p-5">
          <div className="text-[10px] uppercase tracking-[0.2em] font-mono-x text-slate-500 mb-4">Lankytojai per {d.daily.length} d.</div>
          <div className="h-64" data-testid="chart-visitors">
            <ResponsiveContainer>
              <AreaChart data={d.daily}>
                <defs><linearGradient id="g" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#F97316" stopOpacity={0.4} /><stop offset="100%" stopColor="#F97316" stopOpacity={0} /></linearGradient></defs>
                <CartesianGrid stroke="#3B4157" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="day" stroke="#64748B" fontSize={11} />
                <YAxis stroke="#64748B" fontSize={11} allowDecimals={false} />
                <Tooltip {...tip} />
                <Area type="monotone" dataKey="visitors" name="Lankytojai" stroke="#F97316" fill="url(#g)" strokeWidth={2} />
                <Area type="monotone" dataKey="product_views" name="Sėdynių peržiūros" stroke="#0EA5E9" fill="transparent" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="lg:col-span-2 border border-[#3B4157] bg-[#292D3C] p-5">
          <div className="text-[10px] uppercase tracking-[0.2em] font-mono-x text-slate-500 mb-4">Daugiausiai žiūrėtos</div>
          <div className="h-64" data-testid="chart-top-products">
            <ResponsiveContainer>
              <BarChart data={top} layout="vertical" margin={{ left: 10 }}>
                <XAxis type="number" stroke="#64748B" fontSize={11} allowDecimals={false} />
                <YAxis type="category" dataKey="name" stroke="#64748B" fontSize={10} width={110} />
                <Tooltip {...tip} cursor={{ fill: "#212534" }} />
                <Bar dataKey="views" name="Peržiūros" fill="#F97316" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
      <div className="grid lg:grid-cols-5 gap-4 items-start">
        <div className="lg:col-span-2"><Sources items={d.sources} /></div>
        <div className="lg:col-span-3"><Searches items={d.searches || []} /></div>
      </div>
      <div className="border border-[#3B4157] bg-[#292D3C] overflow-x-auto">
        <div className="text-[10px] uppercase tracking-[0.2em] font-mono-x text-slate-500 p-5 pb-0">Peržiūros pagal sėdynę</div>
        <table className="w-full text-sm mt-4" data-testid="product-views-table">
          <thead><tr className="text-left text-slate-500 text-xs uppercase tracking-wider border-b border-[#3B4157]">
            <th className="p-3 pl-5">Sėdynė</th><th className="p-3">Gamintojas</th><th className="p-3 text-right">Peržiūros (laikotarpis)</th><th className="p-3 text-right">Unikalūs žiūrovai</th><th className="p-3 text-right">Iš viso</th><th className="p-3 pr-5 text-right">Likutis</th>
          </tr></thead>
          <tbody>
            {d.products.map((p) => (
              <tr key={p.id} data-testid={`views-row-${p.id}`} className="border-b border-[#3B4157] last:border-0 hover:bg-[#30354A]">
                <td className="p-3 pl-5 flex items-center gap-3"><img src={imgUrl(p.images?.[0])} alt="" className="w-9 h-9 object-contain p-0.5 img-stage" />{p.name}</td>
                <td className="p-3"><BrandBadge brand={p.brand} /></td>
                <td className="p-3 text-right font-mono-x text-orange-500">{p.period_views}</td>
                <td className="p-3 text-right font-mono-x">{p.unique_viewers}</td>
                <td className="p-3 text-right font-mono-x text-slate-500">{p.views || 0}</td>
                <td className="p-3 pr-5 text-right font-mono-x">{p.stock}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
